import { Injectable, inject } from '@angular/core';
import {
    FormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';

import {
    FieldDefinition,
    FormContext,
    FormDefinition,
    FormElementLayoutDefinition,
    FormElementStyle,
    FormElementType,
    FormFieldType,
    FormLayout,
    Trigger,
    WdxDestroyClass,
} from '@wdx/shared/utils';

import {
    IExtendedFieldDefinition,
    ISubFormLayoutFormData,
    ISubFormPendingChange,
} from '../../models';
import { FormConditionsService } from '../form-conditions';
import { FormControlService } from '../form-control/form-control.service';
import { FormFieldsDataService } from '../form-fields-data';
import { FormPendingChangesService } from '../form-pending-changes/form-pending-changes.service';
import { FormStaticService } from '../form-static/form-static.class';
import { FormTriggersService, FormValidationService } from '../form-validation';
import { FormFirstLevelService } from './form-first-level.service';
import {
    CREATE_FORM_GROUP,
    FORM_GROUP,
    MANUAL_FORM_GROUP,
} from './form-builder.static';
import * as R from 'ramda';
import { delay, of, take } from 'rxjs';

@Injectable()
export class FormBuilderService extends WdxDestroyClass {
    private firstLevel = inject(FormFirstLevelService).firstLevel;
    private _formConditionsService = inject(FormConditionsService);
    private fb = inject(UntypedFormBuilder);
    private formValidationService = inject(FormValidationService);
    private formStaticService = inject(FormStaticService);
    private formControlService = inject(FormControlService);
    private formPendingChangesService = inject(FormPendingChangesService);
    private formFieldsDataService = inject(FormFieldsDataService);
    private formTriggersService = inject(FormTriggersService);

    public get formConditionsService(): FormConditionsService {
        return this._formConditionsService;
    }

    buildForm(extraContext?: Record<string, any>): void {
        this.formStaticService.form = this.fb.group(
            this.getFormControlsFromDefinition(
                this.formStaticService.formDefinition,
                this.formStaticService.formLayout,
            ),
        );

        if (extraContext) {
            this.formStaticService.formData.context = R.mergeDeepRight(
                this.formStaticService.formData.context || {},
                extraContext,
            ) as FormContext;
        }

        this.formStaticService?.formLayout?.messageDefinitions?.map(
            (message) => {
                this.formFieldsDataService.messageDefinitionToObject[
                    message.name as string
                ] = message;
            },
        );
    }

    getFormControlsFromDefinition(
        formDefinition: FormDefinition,
        formLayout: FormLayout,
    ): Record<string, any> {
        return {
            ...formDefinition.schema?.reduce(
                (accumulator, formFieldDefinition) => {
                    if (formFieldDefinition.fieldType === 'Array') {
                        const ARR_FORM_GROUP = this.loopFormSchemaData(
                            formFieldDefinition,
                            formLayout?.sectionLayoutDefinitions as FormElementLayoutDefinition[],
                            false,
                            formFieldDefinition.name as string,
                        ) as ISubFormLayoutFormData;

                        if (
                            !this.firstLevel[formFieldDefinition.name as string]
                        ) {
                            this.firstLevel[
                                formFieldDefinition.name as string
                            ] = {} as UntypedFormGroup;
                        }

                        this.firstLevel[formFieldDefinition.name as string] =
                            ARR_FORM_GROUP;

                        return {
                            ...accumulator,
                            [formFieldDefinition.name as string]:
                                ARR_FORM_GROUP.formArray,
                        };
                    }

                    if (
                        MANUAL_FORM_GROUP.includes(
                            formFieldDefinition.fieldType as FormFieldType,
                        )
                    ) {
                        this.formFieldsDataService.addFieldsToNullCheck(
                            this.formStaticService.fieldsToNullCheck,
                            formFieldDefinition.name as string,
                        );
                        const ADDRESS_DATA =
                            this.formStaticService?.formData?.data?.[
                                formFieldDefinition.name as string
                            ];

                        return {
                            ...accumulator,
                            [formFieldDefinition.name as string]: this.fb.group(
                                ADDRESS_DATA ?? {},
                            ),
                        };
                    }

                    return {
                        ...accumulator,
                        [formFieldDefinition.name as string]:
                            this.setFormControlByType(
                                false,
                                formFieldDefinition,
                                this.formStaticService.getLayoutForFieldFromFormDef(
                                    formFieldDefinition.name as string,
                                ) as FormElementLayoutDefinition,
                                {},
                            ),
                    };
                },
                {},
            ),
        };
    }

    setFormData(
        skipData: boolean,
        formFieldDefinition: FieldDefinition,
        isArray = false,
        index = 0,
        name?: string,
        childName?: string,
        childIndex?: number,
    ): any {
        if (
            skipData ||
            typeof this.formStaticService?.formData === 'undefined' ||
            typeof this.formStaticService?.formData?.data === 'undefined' ||
            (typeof this.formStaticService?.formData?.data[
                formFieldDefinition?.name as string
            ] === 'undefined' &&
                !isArray) ||
            (typeof this.formStaticService?.formData?.data[name as string] ===
                'undefined' &&
                isArray)
        ) {
            return null;
        }

        if (!isArray) {
            return this.formStaticService.formData.data[
                formFieldDefinition.name as string
            ];
        }

        return this.getInitialArrayValue(
            formFieldDefinition,
            name as string,
            index,
            childName as string,
            childIndex as number,
            isArray,
        );
    }

    getInitialArrayValue(
        formFieldDefinition: FieldDefinition,
        name: string,
        index: number,
        childName: string,
        childIndex: number,
        isArray: boolean,
    ): any {
        if (childName && isArray) {
            return this.formStaticService.formData.data[name][index][childName][
                childIndex
            ][formFieldDefinition.name as string];
        }
        return this.formStaticService.formData.data[name][index][
            formFieldDefinition.name as string
        ];
    }

    setFormControlByType(
        skipData: boolean,
        formFieldDefinition: FieldDefinition,
        layout: FormElementLayoutDefinition | undefined,
        config: {
            isArray?: boolean;
            index?: number;
            name?: string;
            childName?: string;
            childIndex?: number;
        },
    ): UntypedFormGroup | UntypedFormControl {
        const { isArray, index, name, childName, childIndex } = config;
        const DATA = this.setFormData(
            skipData,
            formFieldDefinition,
            isArray,
            index,
            name,
            childName,
            childIndex,
        );

        if (
            FORM_GROUP.includes(formFieldDefinition.fieldType as FormFieldType)
        ) {
            const formGroup = {
                ...this.createFormGroupForComponent(
                    DATA,
                    formFieldDefinition,
                    layout,
                ),
            };
            return new UntypedFormGroup(
                formGroup,
                this.formValidationService.getValidators(
                    formFieldDefinition,
                    layout,
                ),
            );
        }

        return new UntypedFormControl(
            { value: DATA, disabled: false },
            {
                validators: this.formValidationService.getValidators(
                    formFieldDefinition,
                    layout,
                ),
                updateOn: formFieldDefinition.inputMaskRegEx
                    ? 'blur'
                    : 'change',
            },
        );
    }

    createFormGroupForComponent(
        content: { [key: string]: string | number | boolean },
        formFieldDefinition: FieldDefinition,
        layout?: FormElementLayoutDefinition,
    ): Record<string, UntypedFormControl> {
        const startOnly = layout?.elementStyle?.includes(
            FormElementStyle.StartOnly,
        );
        const childFormControls: { formControlName: string }[] = (
            (CREATE_FORM_GROUP as Record<string, any>)[
                formFieldDefinition.fieldType as string
            ] as { formControlName: string }[]
        ).filter(
            ({ formControlName }) => !startOnly || formControlName === 'start',
        );

        return childFormControls.reduce(
            (accumulator, current) => {
                return {
                    ...accumulator,
                    [current.formControlName]: new UntypedFormControl(
                        {
                            value:
                                content == null
                                    ? content
                                    : typeof content[
                                            current.formControlName
                                        ] !== 'undefined'
                                      ? content[current.formControlName]
                                      : undefined,
                            disabled: false,
                        },
                        [
                            FormFieldType.DateRange,
                            FormFieldType.DateTimeRange,
                        ].includes(
                            formFieldDefinition?.fieldType as FormFieldType,
                        )
                            ? {
                                  validators:
                                      this.formValidationService.getDateValidators(
                                          formFieldDefinition,
                                          layout,
                                      ),
                              }
                            : {},
                    ),
                };
            },
            {} as Record<string, UntypedFormControl>,
        );
    }

    findDefinition(name: string, parent?: string): FieldDefinition[] {
        let i = 0;
        const MAX = this.formStaticService.formDefinition.schema
            ?.length as number;
        const SCHEMA = this.formStaticService.formDefinition
            .schema as FieldDefinition[];
        let res: any[] = [];

        while (i < MAX) {
            if (SCHEMA[i].name === name && !parent) {
                res = SCHEMA[i].childSchema as FieldDefinition[];
                i = MAX;
            }

            if (parent && SCHEMA[i].name === parent) {
                SCHEMA[i].childSchema?.forEach((childDefinition) => {
                    if (childDefinition.name === name) {
                        res = childDefinition.childSchema as FieldDefinition[];
                        i = MAX;
                    }
                });
            }

            i++;
        }

        return res;
    }

    loopFormSchemaData(
        data: IExtendedFieldDefinition,
        layout: FormElementLayoutDefinition[],
        skipData: boolean,
        name?: string,
        parentName?: string,
        parentIndex?: number,
    ): ISubFormLayoutFormData {
        const CREATED_FORM_GROUP = this.createFormArrayGroup(
            data.childSchema as IExtendedFieldDefinition[],
            layout,
            skipData,
            name,
            parentName,
            parentIndex,
        );

        return {
            formArray: this.fb.array(
                CREATED_FORM_GROUP.formGroup,
                this.formValidationService.getValidators(data),
            ),
            layout: CREATED_FORM_GROUP.formGroupLayout.map((fgl) => ({
                elementType: FormElementType.Field,
                ...fgl,
            })),
            pendingChanges: CREATED_FORM_GROUP.pendingChanges,
        };
    }

    createFormArrayGroup(
        data: IExtendedFieldDefinition[],
        layout: FormElementLayoutDefinition[],
        skipData: boolean,
        name?: string,
        parentName?: string,
        parentIndex?: number,
    ): {
        formGroup: UntypedFormGroup[];
        formGroupLayout: FieldDefinition[];
        pendingChanges: { [key: number]: ISubFormPendingChange };
    } {
        const FORM_ARRAY_DATA = {
            formGroup: [] as UntypedFormGroup[],
            formGroupLayout: [] as IExtendedFieldDefinition[][],
            pendingChanges: {} as any,
        };

        const DATA = [...data];

        // This is checks if we have form data to populate the form with and creates the amount of groups needed.
        if (
            !skipData &&
            (this.formStaticService?.formData?.data?.[name as string] ||
                this.formStaticService?.formData?.data?.[
                    parentName as string
                ]?.[0]?.[name as string])
        ) {
            if (!parentName) {
                const ARR =
                    this.formStaticService.formData.data[name as string];
                ARR.map((formDataResult: any, index: number) => {
                    FORM_ARRAY_DATA.formGroup.push(
                        this.createFormGroup(
                            data,
                            layout,
                            false,
                            name,
                            index,
                            parentName,
                            index,
                        ),
                    );

                    // TODO - remove ? seems to do nothing ?
                    this.formPendingChangesService.addDataToSubFormPendingChanges(
                        name as string,
                        index,
                        formDataResult,
                    );

                    FORM_ARRAY_DATA.pendingChanges[index] = formDataResult;

                    FORM_ARRAY_DATA.formGroupLayout.push(DATA);
                });
            }

            if (parentName) {
                const ARR =
                    this.formStaticService.formData.data[parentName][
                        parentIndex as number
                    ];
                if (ARR && ARR[name as string]) {
                    ARR[name as string].map((_: any, indexChild: number) => {
                        FORM_ARRAY_DATA.formGroup.push(
                            this.createFormGroup(
                                data,
                                layout,
                                false,
                                name,
                                indexChild,
                                parentName,
                                parentIndex,
                            ),
                        );

                        FORM_ARRAY_DATA.formGroupLayout.push(data);
                    });
                }
            }

            return FORM_ARRAY_DATA as {
                formGroup: UntypedFormGroup[];
                formGroupLayout: FieldDefinition[];
                pendingChanges: { [key: number]: ISubFormPendingChange };
            };
        }

        return FORM_ARRAY_DATA as {
            formGroup: UntypedFormGroup[];
            formGroupLayout: FieldDefinition[];
            pendingChanges: { [key: number]: ISubFormPendingChange };
        };
    }

    createFormGroup(
        data: FieldDefinition[],
        layout: FormElementLayoutDefinition[],
        skipData: boolean,
        name?: string,
        index?: number,
        parentName?: string,
        parentIndex?: number,
    ): UntypedFormGroup {
        return this.fb.group({
            ...data.reduce((accumulator, formFieldDefinition) => {
                if (formFieldDefinition.fieldType === FormFieldType.Array) {
                    const ARR_FORM_GROUP = this.loopFormSchemaData(
                        formFieldDefinition,
                        layout,
                        skipData,
                        formFieldDefinition.name as string,
                        name,
                        index,
                    );

                    return {
                        ...accumulator,
                        [formFieldDefinition.name as string]:
                            ARR_FORM_GROUP.formArray,
                    };
                }

                if (
                    MANUAL_FORM_GROUP.includes(
                        formFieldDefinition.fieldType as FormFieldType,
                    )
                ) {
                    this.formFieldsDataService.addFieldsToNullCheck(
                        this.formStaticService.fieldsToNullCheck,
                        formFieldDefinition.name as string,
                    );
                    return {
                        ...accumulator,
                        [formFieldDefinition.name as string]: this.fb.group({}),
                    };
                }

                const fieldLayout =
                    this.formStaticService.getLayoutForFieldFromFormDef(
                        `${name}.${formFieldDefinition.name}`,
                    ) as FormElementLayoutDefinition;
                return {
                    ...accumulator,
                    [formFieldDefinition.name as string]: parentName
                        ? this.setFormControlByType(
                              skipData,
                              formFieldDefinition,
                              fieldLayout,
                              {
                                  isArray: true,
                                  index: parentIndex,
                                  name: parentName,
                                  childName: name,
                                  childIndex: index,
                              },
                          )
                        : this.setFormControlByType(
                              skipData,
                              formFieldDefinition,
                              fieldLayout,
                              {
                                  isArray: true,
                                  index,
                                  name,
                              },
                          ),
                };
            }, {}),
        });
    }

    applyTriggersToChildControls(
        triggers: Trigger[] | null | undefined,
        formGroupPath: string,
        newFormGroup: FormGroup,
        newIndex?: number,
    ) {
        const pathWithoutSubformIndex = formGroupPath?.replaceAll(
            /\[\d*]/g,
            '',
        );
        triggers?.forEach((trigger) => {
            // triggers that update new child controls
            if (
                trigger.is?.some((is) =>
                    is?.then?.updateFields?.some(({ name }) =>
                        name?.startsWith(`${pathWithoutSubformIndex}.`),
                    ),
                )
            ) {
                let triggerFieldValue;
                // trigger is also from the same parent (subform or address)
                if (trigger.field?.startsWith(`${pathWithoutSubformIndex}.`)) {
                    const triggerFieldParts = trigger.field.split('.');
                    triggerFieldValue =
                        triggerFieldParts.length === 2
                            ? newFormGroup.controls[triggerFieldParts[1]]?.value
                            : (
                                  newFormGroup.controls[
                                      triggerFieldParts[1]
                                  ] as FormGroup
                              )?.controls[triggerFieldParts[2]]?.value;
                } else if (this.formTriggersService.useContext(trigger)) {
                    triggerFieldValue =
                        this.formTriggersService.getContextTriggerValue(
                            trigger,
                            this.formStaticService?.formData?.context ?? {},
                        );
                } else {
                    // trigger is not from current parent - e.g. a root level control
                    triggerFieldValue =
                        this.formControlService.getFormControlData(
                            this.formStaticService.form,
                            trigger.field as string,
                        )?.[0]?.formControl?.value;
                }

                this._formConditionsService.applyIsTrigger(
                    trigger,
                    triggerFieldValue,
                    {
                        index: newIndex,
                        formControl: undefined as any,
                    },
                );
            }

            //triggers to add to new controls
            if (trigger.field?.startsWith(`${pathWithoutSubformIndex}.`)) {
                const triggerFieldParts = trigger.field.split('.');

                if (triggerFieldParts.length > 1) {
                    const leafName = R.last(triggerFieldParts);
                    const triggerControl =
                        newFormGroup.controls[leafName as string];
                    if (triggerControl) {
                        this._formConditionsService.setFormControlListener(
                            trigger,
                            {
                                index: newIndex,
                                formControl: triggerControl,
                            },
                        );
                    }
                }
            }
        }, 0);

        const childFieldDefaults =
            this.formStaticService.formDefinition?.conditions?.defaults?.filter(
                ({ field }) => field?.startsWith(`${pathWithoutSubformIndex}.`),
            );

        of(null)
            .pipe(delay(0), delay(0), take(1))
            .subscribe(() => {
                const childVisibilityRules =
                    this.formStaticService.formDefinition?.conditions?.visibility?.filter(
                        (visRule) =>
                            visRule?.field?.startsWith(
                                `${pathWithoutSubformIndex}.`,
                            ),
                    );
                this._formConditionsService.updateFormVisibilityAndListeners(
                    childVisibilityRules,
                    newIndex,
                );
                this._formConditionsService.setDefaultFieldValues(
                    childFieldDefaults,
                    newIndex,
                );
            });
    }
}
